import { getMomentTz } from "@dexteel/mesf-core";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const moment = getMomentTz();

/** Resolved once, when the module loads, so an absent parameter always reads back the same instant. */
export const DEFAULT_START: Date = (() => {
	const date = new Date();
	date.setDate(1); // First day of the current month.
	return date;
})();

export const DEFAULT_END: Date = moment()
	.hour(23)
	.minute(59)
	.second(59)
	.millisecond(0)
	.toDate();

/**
 * `toISOString`, never `toJSON`: the application replaces `Date.prototype.toJSON` with one that
 * prints server-local wall time and no zone, so writing the URL with it and reading it back gives a
 * different instant. See the same note in `work-orders/hooks/useWorkOrdersFilters.ts`.
 */
export const DEFAULT_START_PARAM = encodeURIComponent(
	DEFAULT_START.toISOString(),
);
export const DEFAULT_END_PARAM = encodeURIComponent(DEFAULT_END.toISOString());

export type FormsFilters = {
	/** null while the route has no usable area, which is what keeps the query from running. */
	assetId: number | null;
	start: Date;
	end: Date;
	/** The dates exactly as they sit in the URL; the query is keyed on these, not on the parsed ones. */
	startParam: string;
	endParam: string;
	searchText: string;
	statusCode: string;
	includeExpired: boolean;
};

const STATUS_CODES = ["All", "O", "S", "A"];

const readDate = (param: string, fallback: Date): Date => {
	const parsed = moment(decodeURIComponent(param));
	return parsed.isValid() ? parsed.toDate() : fallback;
};

/**
 * The filters, read straight from the URL and the route on every render. See the note in
 * `task-list/hooks/useTaskListFilters.ts`.
 */
export const useFormsFilters = (): FormsFilters => {
	const [searchParams] = useSearchParams();
	const { AssetIdParam } = useParams();

	const start = searchParams.get("start");
	const end = searchParams.get("end");
	const search = searchParams.get("search");
	const status = searchParams.get("status");
	const expired = searchParams.get("expired");

	return useMemo(() => {
		const assetId = Number(AssetIdParam);
		const startParam = start ?? DEFAULT_START_PARAM;
		const endParam = end ?? DEFAULT_END_PARAM;

		return {
			assetId: Number.isInteger(assetId) ? assetId : null,
			start: readDate(startParam, DEFAULT_START),
			end: readDate(endParam, DEFAULT_END),
			startParam,
			endParam,
			searchText: search ?? "",
			statusCode: status && STATUS_CODES.includes(status) ? status : "All",
			includeExpired: expired === "true",
		};
	}, [AssetIdParam, start, end, search, status, expired]);
};
